import { useState, useRef, useMemo, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StatusPill from '../../../Shared/Components/StatusPill';
import UpdateStatusButtonModal from './UpdateStatusButtonModal';
import { PROCESSED_TIMESHEET_STATUS_LABELS } from '../../constants';
import useAdminAuthContext from '../../hooks/useAdminAuthContext';
import useToast from '../../../utils/useToast';

const STATUS_OPTIONS = ['BPReview', 'BPQuestion', 'Cancelled', 'Resolved', 'TCHold', 'TCReview'];

const UpdateStatusButton = ({
    id,
    status: initialStatus,
    needsReviewStatus: initialNeedsReviewStatus,
    variant = 'lt-admin-pill',
    clinicianName = '',
    readOnly = false,
}) => {
    const { isTimesheetAdminUser, isTimesheetSystemAdminUser } = useAdminAuthContext();
    const statusButtonRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(initialNeedsReviewStatus || initialStatus);
    const [newStatus, setNewStatus] = useState();
    const { renderToast, showSuccessToast } = useToast();

    useEffect(() => {
        setCurrentStatus(initialNeedsReviewStatus || initialStatus);
    }, [initialNeedsReviewStatus, initialStatus]);

    const toggleChangeStatusMenu = () => {
        setMenuOpen((prev) => !prev);
    };
    const closeMenu = () => {
        setMenuOpen(false);
    };
    const handleChangeStatus = (newStatusProp) => {
        setNewStatus(newStatusProp);
        closeMenu();
        setOpenConfirmationModal(true);
    };

    const handleChangeStatusSuccess = (status) => {
        setCurrentStatus(status);
        setNewStatus(undefined);
        setOpenConfirmationModal(false);
        showSuccessToast(
            `${
                clinicianName ? `${clinicianName}'s ` : ''
            }processed timesheet status was successfully updated.`
        );
    };

    const filteredStatusOptions = useMemo(
        () => STATUS_OPTIONS.filter((status) => status !== currentStatus),
        [currentStatus]
    );

    const checkHasEditPermission = () => {
        if (isTimesheetAdminUser || isTimesheetSystemAdminUser) {
            if (currentStatus === 'Posted') {
                return false;
            }
            return true;
        }
        return false;
    };

    const hasEditPermission = checkHasEditPermission();

    return (
        <>
            {renderToast()}
            <StatusPill
                status={currentStatus}
                variant={variant}
                data-testid="current-status-pill"
                content={
                    <>
                        {PROCESSED_TIMESHEET_STATUS_LABELS[currentStatus]}
                        {hasEditPermission && !readOnly && (
                            <KeyboardArrowDownIcon
                                sx={{
                                    ml: 'auto',
                                    transition: 'transform 0.3s',
                                    transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        )}
                    </>
                }
                title="Change status"
                size="small"
                sx={{ minWidth: 146 }}
                ref={statusButtonRef}
                onClick={hasEditPermission && !readOnly ? toggleChangeStatusMenu : undefined}
                disabled={!id || !currentStatus}
                aria-controls={menuOpen ? 'change-status-options' : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? 'true' : undefined}
            />
            <Menu
                id="change-status-options"
                data-testid="change-status-options"
                slotProps={{ list: { 'aria-labelledby': 'Change status options' } }}
                anchorEl={statusButtonRef.current}
                open={menuOpen}
                onClose={closeMenu}
            >
                {filteredStatusOptions.map((state) => (
                    <MenuItem key={state} onClick={() => handleChangeStatus(state)} disableRipple>
                        <StatusPill status={state} sx={{ minWidth: 146 }} />
                    </MenuItem>
                ))}
            </Menu>
            {openConfirmationModal && (
                <UpdateStatusButtonModal
                    id={id}
                    onClose={() => setOpenConfirmationModal(false)}
                    currentStatus={currentStatus}
                    newStatus={newStatus}
                    setNewStatus={setNewStatus}
                    onSuccess={handleChangeStatusSuccess}
                    statusOptions={filteredStatusOptions}
                />
            )}
        </>
    );
};

export default UpdateStatusButton;
